import {
  DropDown,
  DropDownIconContainer,
  DropDownTitleContainer,
  MenuOverlay,
} from './DropDownMenu.styles'
import { computeMenuFit, MenuFit, NO_FIT, sameFit } from './menuFit'
import { Motion, spring } from 'react-motion'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaAngleDown, FaEllipsisV } from 'react-icons/fa'

import Overlay from '@app/overlay/Overlay'
import { State } from '@app/state/types'
import { useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { Theme } from '../theme'
import { useSnackbar } from '@app/snackbar'

const DD_SPRING_SETTINGS = { stiffness: 70, damping: 20 }
const DD_SPRING_TO_VALUE = 10
const DD_OFFSET_Y = -60
const DD_DEFAULT_WIDTH = 100
const DD_CLDRAWER_OFFSET_X = 0
const DD_ICON_SIZE = 14

// Lets menu items close the menu (e.g. terminal actions) without the parent having
// to control open state. Provided by DropDownMenu, consumed by its children.
export const DropDownMenuContext = React.createContext<{ close: () => void }>({
  close: () => undefined,
})

interface DropDownMenuProps {
  value?: string | number | undefined
  children?: React.ReactNode
  width?: number
  noArrow?: boolean
  renderValue?: () => JSX.Element
  zIndex?: number
  style?: React.CSSProperties
  backgroundColor?: string
  color?: string
  iconColor?: string
  relative?: boolean
  reference?: React.MutableRefObject<HTMLDivElement>
  rounded?: boolean
  marginLeft?: boolean
  marginRight?: boolean
  marginTop?: boolean
  tablet?: boolean
  visible?: boolean
  disabledState?: {
    disabled: boolean
    message: string
  }
  x?: number
  y?: number
  xAdj?: number
  yAdj?: number
  useShadow?: boolean
  isMenu?: boolean
  index?: number
  clampToViewport?: boolean
}

const DropDownMenu = ({
  x = 0,
  y = 0,
  xAdj = 0,
  yAdj = 40,
  value = undefined,
  children,
  width = DD_DEFAULT_WIDTH,
  noArrow = false,
  renderValue,
  zIndex = 12000,
  style,
  backgroundColor,
  color,
  iconColor,
  reference,
  relative,
  tablet = false,
  rounded = false,
  visible,
  disabledState,
  useShadow = true,
  isMenu = false,
  index,
  clampToViewport = false,
}: DropDownMenuProps) => {
  const theme = useTheme() as Theme
  const [menuOpen, setMenuState] = useState<boolean>(false)
  const menuItemRef = reference ? reference : useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState<MenuFit>(NO_FIT)
  const fitRef = useRef<MenuFit>(NO_FIT)
  const [openSnackbar] = useSnackbar()
  // Three-dot menus live in table rows and headers all over the app, so they are the
  // ones that open next to an edge. Other dropdowns can ask for the same treatment.
  const shouldClamp = clampToViewport || isMenu
  const YValue = (xx: number) => (relative ? 0 : DD_OFFSET_Y + (xx + x) / 2)
  const motionParams = {
    x: spring(DD_SPRING_TO_VALUE, DD_SPRING_SETTINGS),
  }
  const bounds = menuItemRef.current?.getBoundingClientRect()
  const motionTransform = (xx: number) => `
    translate3d(${fit.dx}px, ${YValue(xx + x) + fit.dy}px, 100px) 
  `

  const _x = bounds
    ? window.innerWidth - bounds.width - width - DD_CLDRAWER_OFFSET_X
    : 0

  // Pin the fixed overlay to the trigger instead of letting it fall where it happens
  // to sit in the flow: xAdj/yAdj still shift it from there, but the position now
  // survives being portalled out to <body> and any transformed ancestor.
  const anchorLeft = () => {
    if (!shouldClamp || !bounds) return undefined
    // Menus that asked for clamping were laid out right-aligned to their trigger,
    // the three-dot menus from its left edge. Keep each one where it has been.
    return clampToViewport ? bounds.right - width : bounds.left
  }
  const anchoredLeft = anchorLeft()

  useEffect(() => {
    if (visible !== undefined) {
      setMenuState(visible)
    }
  }, [visible])

  const applyFit = (next: MenuFit) => {
    if (sameFit(fitRef.current, next)) return
    fitRef.current = next
    setFit(next)
  }

  const measureFit = () => {
    const menu = menuRef.current
    const trigger = menuItemRef.current
    if (!menu || !trigger) return

    const applied = fitRef.current
    const menuRect = menu.getBoundingClientRect()
    const triggerRect = trigger.getBoundingClientRect()

    applyFit(
      computeMenuFit({
        // Take back the correction already on the element so we always reason about
        // where the menu wants to be, not about where we last moved it.
        menu: {
          left: menuRect.left - applied.dx,
          top: menuRect.top - applied.dy,
          bottom: menuRect.bottom - applied.dy,
          width: menuRect.width,
        },
        trigger: triggerRect,
        contentHeight: applied.maxHeight ? menu.scrollHeight : menuRect.height,
        appliedMaxHeight: applied.maxHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      })
    )
  }

  useLayoutEffect(() => {
    if (!shouldClamp || !menuOpen) {
      applyFit(NO_FIT)
      return
    }

    measureFit()

    // Submenus expand inline, so the menu can outgrow the screen after it opened.
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => measureFit())
        : null
    if (menuRef.current) observer?.observe(menuRef.current)
    window.addEventListener('resize', measureFit)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measureFit)
    }
  }, [shouldClamp, menuOpen])

  const handleOpen = () => {
    if (disabledState?.disabled) {
      return openSnackbar(disabledState?.message)
    }
    setMenuState(!menuOpen)
  }

  function getIcon() {
    return isMenu ? (
      <FaEllipsisV
        size={DD_ICON_SIZE}
        color={iconColor ? iconColor : theme.colors.secondary}
      />
    ) : (
      <FaAngleDown
        size={DD_ICON_SIZE}
        color={iconColor ? iconColor : theme.colors.secondary}
      />
    )
  }

  const val = renderValue ? renderValue() : value

  const menuContent = menuOpen ? (
    <>
      <Overlay zIndex={zIndex} closeAction={() => setMenuState(false)} />
      <Motion defaultStyle={{ x: 0 }} style={motionParams}>
        {({ x }) => (
          <MenuOverlay
            tablet={tablet}
            transform={motionTransform(x)}
            x={x + _x}
            y={relative ? y + (bounds?.y ?? 0) : y + (bounds?.y ?? 0)}
            xAdj={xAdj}
            yAdj={yAdj}
            width={width}
            zIndex={zIndex}
            relative={relative}
            left={anchoredLeft}
            maxHeight={fit.maxHeight}
            ref={menuRef}
          >
            <DropDownMenuContext.Provider
              value={{ close: () => setMenuState(false) }}
            >
              {children}
            </DropDownMenuContext.Provider>
          </MenuOverlay>
        )}
      </Motion>
    </>
  ) : null

  return (
    <div style={{ zIndex: index ? zIndex - 1 : 12000 }}>
      {/* When clamped, portal to <body> so the fixed overlay escapes per-row sticky
          stacking contexts (e.g. the tee StickyContainer) and isn't painted under
          neighbouring items. Position is unchanged — it's anchored to the trigger. */}
      {shouldClamp && menuContent
        ? createPortal(menuContent, document.body)
        : menuContent}
      <DropDown
        ref={menuItemRef}
        rounded={rounded}
        onClick={handleOpen}
        style={style}
        backgroundColor={backgroundColor}
        color={color}
        useShadow={useShadow}
      >
        {val && <DropDownTitleContainer>{val}</DropDownTitleContainer>}
        <DropDownIconContainer noArrow={noArrow} hasTitle={!!val} isOpen={menuOpen} isMenu={isMenu}>
          {!noArrow && getIcon()}
        </DropDownIconContainer>
      </DropDown>
    </div>
  )
}

export { DropDownMenu }
